import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Home from "@/pages/Home";
import ScrollToTop from "@/components/ScrollToTop";
import GaTracker from "@/components/GaTracker";
import EmailGate from "@/components/EmailGate";
import { Toaster } from "@/components/ui/sonner";
import { DirectoryProvider } from "@/components/directory/DirectoryContext";

// Code-split sub-pages — they load only when the user navigates to them
const About          = lazy(() => import("@/pages/About"));
const Leadership     = lazy(() => import("@/pages/Leadership"));
const ModiCase       = lazy(() => import("@/pages/ModiCase"));
const GlobalCouncil  = lazy(() => import("@/pages/GlobalCouncil"));
// `/collaborate` legacy route now resolves to Partners (the strategy-rebuilt
// organizational pathway). Old Collaborate.jsx file kept on disk but
// unmounted from the router.
const Media          = lazy(() => import("@/pages/Media"));
const Gallery        = lazy(() => import("@/pages/Gallery"));
const NominateHero   = lazy(() => import("@/pages/NominateHero"));
const OurFounder     = lazy(() => import("@/pages/OurFounder"));
const RandomActs     = lazy(() => import("@/pages/RandomActs"));
const CommunityWall  = lazy(() => import("@/pages/CommunityWall"));
const YogaDay        = lazy(() => import("@/pages/YogaDay"));
const Stories        = lazy(() => import("@/pages/Stories"));
const VerifyDownload = lazy(() => import("@/pages/VerifyDownload"));
const VerifyEmail    = lazy(() => import("@/pages/VerifyEmail"));
const VerifyAudience = lazy(() => import("@/pages/VerifyAudience"));
const PublicStatus   = lazy(() => import("@/pages/PublicStatus"));
const Contact        = lazy(() => import("@/pages/Contact"));
const Summit0808Page = lazy(() => import("@/pages/Summit0808Page"));
const GetInvolved    = lazy(() => import("@/pages/GetInvolved"));
const ContributePage = lazy(() => import("@/pages/Contribute"));
const PrivacyPolicy  = lazy(() => import("@/pages/PrivacyPolicy"));
const VoiceOfConsciousness = lazy(() => import("@/pages/VoiceOfConsciousness"));
const OneConsciousAct = lazy(() => import("@/pages/OneConsciousAct"));
const ConsciousSuperApp = lazy(() => import("@/pages/ConsciousSuperApp"));
const AdminVoices    = lazy(() => import("@/pages/AdminVoices"));
const AdminRandomActs = lazy(() => import("@/pages/AdminRandomActs"));
const AdminCommunityActs = lazy(() => import("@/pages/AdminCommunityActs"));
const AdminSignatures = lazy(() => import("@/pages/AdminSignatures"));
const AdminAmbassadors = lazy(() => import("@/pages/AdminAmbassadors"));
const AdminCategories = lazy(() => import("@/pages/AdminCategories"));
const AdminAudience = lazy(() => import("@/pages/AdminAudience"));
const AdminBulkImport = lazy(() => import("@/pages/AdminBulkImport"));
const AdminPartners = lazy(() => import("@/pages/AdminPartners"));
const Ambassadors = lazy(() => import("@/pages/Ambassadors"));
const Partners = lazy(() => import("@/pages/Partners"));
const NotFound       = lazy(() => import("@/pages/NotFound"));
const ActPermalink   = lazy(() => import("@/pages/ActPermalink"));
const PrinciplePage  = lazy(() => import("@/pages/PrinciplePage"));
const LuminaryUpload = lazy(() => import("@/pages/LuminaryUpload"));
const AdminLuminaries = lazy(() => import("@/pages/AdminLuminaries"));
const AdminAnalytics = lazy(() => import("@/pages/AdminAnalytics"));
const AdminMovements = lazy(() => import("@/pages/AdminMovements"));
const MyDashboard = lazy(() => import("@/pages/MyDashboard"));
const DirectoryPage = lazy(() => import("@/pages/directory/DirectoryPage"));
const OrganisationProfile = lazy(() => import("@/pages/directory/OrganisationProfile"));
const WatchFeed = lazy(() => import("@/pages/directory/WatchFeed"));
const DirectoryAccess = lazy(() => import("@/pages/directory/DirectoryAccess"));
const DirectorySubmit = lazy(() => import("@/pages/directory/DirectorySubmit"));
const DirectoryResubmit = lazy(() => import("@/pages/directory/DirectoryResubmit"));
const DirectoryAction = lazy(() => import("@/pages/directory/DirectoryAction"));
const DirectoryDashboard = lazy(() => import("@/pages/directory/DirectoryDashboard"));
const DirectoryConversation = lazy(() => import("@/pages/directory/DirectoryConversation"));
const OrganisationManage = lazy(() => import("@/pages/directory/OrganisationManage"));
const AdminDirectory = lazy(() => import("@/pages/directory/AdminDirectory"));

function PageFallback() {
  return (
    <div className="min-h-screen bg-docm-cream flex items-center justify-center">
      <div className="flex items-center gap-3 text-docm-inkSoft">
        <span className="w-2 h-2 rounded-full bg-docm-moss animate-pulse" />
        <span className="text-sm uppercase tracking-[0.2em]">Loading…</span>
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <DirectoryProvider>
        <ScrollToTop />
        <GaTracker />
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/directory" element={<DirectoryPage />} />
            <Route path="/directory/watch" element={<WatchFeed />} />
            <Route path="/directory/sign-in" element={<DirectoryAccess />} />
            <Route path="/directory/verify" element={<DirectoryAccess verify />} />
            <Route path="/directory/add" element={<DirectorySubmit />} />
            <Route path="/directory/submissions/:id" element={<DirectoryResubmit />} />
            <Route path="/directory/dashboard" element={<DirectoryDashboard />} />
            <Route path="/directory/manage/:id" element={<OrganisationManage />} />
            <Route path="/directory/conversations/:id" element={<DirectoryConversation />} />
            <Route path="/directory/:slug/claim" element={<DirectoryAction claim />} />
            <Route path="/directory/:slug/connect" element={<DirectoryAction />} />
            <Route path="/directory/:slug" element={<OrganisationProfile />} />
            <Route path="/admin/directory/*" element={<AdminDirectory />} />
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            {/* Legacy URLs — About and 9 Principles are now one merged page */}
            <Route path="/the-story" element={<Navigate to="/about" replace />} />
            <Route path="/9-principles" element={<Navigate to="/about#principles" replace />} />
            <Route path="/leadership" element={<Leadership />} />
            <Route path="/modi-case" element={<ModiCase />} />
            <Route path="/global-council" element={<GlobalCouncil />} />
            <Route path="/media" element={<Media />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/nominate-hero" element={<NominateHero />} />
            <Route path="/our-founder" element={<OurFounder />} />
            <Route path="/random-acts" element={<RandomActs />} />
            <Route path="/random-acts-of-consciousness" element={<RandomActs />} />
            <Route path="/community-acts" element={<CommunityWall />} />
            <Route path="/community-wall" element={<CommunityWall />} />
            <Route path="/yoga-day" element={<YogaDay />} />
            <Route path="/stories" element={<Stories />} />
            {/* 2026-07-27 · Event date shifted from 08·08 to 11·11.
                `/1111` is now the canonical route. `/0808` stays as a
                permanent redirect so any external link, printed material
                or bookmarked URL still lands the visitor on the right page. */}
            <Route path="/1111" element={<Summit0808Page />} />
            <Route path="/0808" element={<Navigate to="/1111" replace />} />
            <Route path="/get-involved" element={<GetInvolved />} />
            <Route path="/contribute" element={<ContributePage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/voice-of-consciousness" element={<VoiceOfConsciousness />} />
            <Route path="/one-conscious-act" element={<OneConsciousAct />} />
            <Route path="/conscious-super-app" element={<ConsciousSuperApp />} />
            <Route path="/super-app" element={<ConsciousSuperApp />} />
            <Route path="/cness" element={<ConsciousSuperApp />} />
            <Route path="/admin/voices" element={<AdminVoices />} />
            <Route path="/admin/random-acts" element={<AdminRandomActs />} />
            <Route path="/admin/community-acts" element={<AdminCommunityActs />} />
            <Route path="/admin/signatures" element={<AdminSignatures />} />
            <Route path="/admin/ambassadors" element={<AdminAmbassadors />} />
            <Route path="/admin/categories" element={<AdminCategories />} />
            <Route path="/admin/audience" element={<AdminAudience />} />
            <Route path="/admin/bulk-import" element={<AdminBulkImport />} />
            <Route path="/admin/partners" element={<AdminPartners />} />
            <Route path="/ambassadors" element={<Ambassadors />} />
            <Route path="/global-ambassadors" element={<Ambassadors />} />
            <Route path="/partners" element={<Partners />} />
            {/* Collaborate kept as alias of /partners — both URLs work */}
            <Route path="/collaborate" element={<Partners />} />
            <Route path="/verify-download" element={<VerifyDownload />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/verify-audience/:token" element={<VerifyAudience />} />
            <Route path="/status/:reference_number" element={<PublicStatus />} />
            {/* Permalinks & new pages — 2026-07-16 · Tiger's social-feed push */}
            <Route path="/act/:ref" element={<ActPermalink />} />
            <Route path="/principles/:slug" element={<PrinciplePage />} />
            <Route path="/luminary-upload" element={<LuminaryUpload />} />
            <Route path="/admin/luminaries" element={<AdminLuminaries />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/movements" element={<AdminMovements />} />
            <Route path="/my-dashboard" element={<MyDashboard />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </DirectoryProvider>
      </BrowserRouter>
      <EmailGate />
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
